<script setup lang="ts">
import { useCurrentDiveCenter } from '@/composables/useCurrentDiveCenter'
import { usePreferences } from '@/composables/usePreferences'
import { useSubscription } from '@/composables/useSubscription'
import type { SubscriptionTier } from '@/api/subscription'

/*
 * `comingSoon`: la pantalla está entera, pero el cobro por volumen todavía no se
 * anuncia (§60). El guard rebota a Settings; el menú y la tarjeta ya la muestran
 * apagadas con "Soon". Se enciende quitando esta línea del `meta`.
 */
definePage({ meta: { requiresAuth: true, comingSoon: true } })

/**
 * El cupo del CENTRO seleccionado y cuánto lleva usado (§33).
 *
 * La suscripción es por centro: la misma cuenta puede tener uno en Silver y otro
 * en Free, así que lo que se ve acá depende del selector de la cabecera.
 *
 * Es una pantalla de LECTURA: el plan lo asigna un admin de Blue Trails, no se
 * contrata desde acá. Por eso no hay botón de comprar — habría que sostener una
 * promesa que el portal todavía no puede cumplir.
 */
const { subscription, loading, unavailable, loadSubscription } = useSubscription()
const { currentId, current } = useCurrentDiveCenter()
const { formatDate, formatCurrency } = usePreferences()

/*
 * 🪤 Es una pantalla de la cuenta —vive en Settings— así que el `:key` del
 * `RouterView` no la remonta al cambiar de centro desde la cabecera (§23.3). Sin
 * este watcher seguiría mostrando el cupo del centro anterior, que es de otro
 * plan y de otro medidor.
 */
watch(currentId, () => loadSubscription(), { immediate: true })

const usage = computed(() => subscription.value?.usage)
const cap = computed(() => subscription.value?.tier.annualBookings ?? null)

const ratio = computed(() => {
  if (!usage.value || !cap.value) return 0

  return usage.value.seats / cap.value
})

const barColor = computed(() => {
  if (usage.value?.state === 'blocked') return 'error'
  if (usage.value?.state === 'warning') return 'warning'

  return 'primary'
})

/**
 * La barra se topea en 100 y el número de al lado no.
 *
 * Una barra que se pasa del borde no dice cuánto se pasó; el porcentaje sí, y es
 * el dato que importa justo cuando ya se pasó.
 */
const barValue = computed(() => Math.min(100, Math.round(ratio.value * 100)))

const percent = computed(() => Math.round(ratio.value * 100))

/** El consejo, ya redactado. `null` cuando no hay nada que decir todavía. */
const advice = computed<{ text: string; hint?: string } | null>(() => {
  const u = usage.value

  if (!u) return null

  const suggested = u.suggestedTierSlug
    ? subscription.value?.tiers.find(t => t.slug === u.suggestedTierSlug)?.displayName
    : undefined

  /*
   * La proyección desde pocas semanas no absorbe la estacionalidad, y decirlo
   * cambia cuánto hay que creerle: un centro que hace su año entero en cuatro
   * meses de verano proyectado desde el verano parece necesitar el doble.
   */
  const hint = u.projectionBasis === 'recent_weeks'
    ? `Based on the last ${u.weeksObserved} weeks, not a full year yet.`
    : undefined

  switch (u.advice) {
    case 'not_enough_data':
      return { text: 'Not enough history yet to tell you where this year is heading.' }
    case 'upgrade':
      return {
        text: `At this rate you will use about ${u.projectedAnnual} bookings this year`
          + `${suggested ? `, which is ${suggested} territory` : ''}.`,
        hint,
      }
    case 'downgrade':
      return {
        text: `At this rate you will use about ${u.projectedAnnual} bookings this year`
          + `${suggested ? `, which fits in ${suggested}` : ''}.`,
        hint,
      }
    default:
      return {
        text: `At this rate you will use about ${u.projectedAnnual} bookings this year, `
          + 'which your current plan covers.',
        hint,
      }
  }
})

/** Las últimas doce semanas con actividad, para ver el pulso sin un gráfico. */
const recentWeeks = computed(() => (usage.value?.weeks ?? []).slice(-12))

const busiestWeek = computed(() =>
  recentWeeks.value.reduce((max, w) => Math.max(max, w.seats), 0))

function tierPrice(tier: SubscriptionTier): string {
  if (tier.annualPrice <= 0) return 'Free'

  return `${formatCurrency(tier.annualPrice, tier.currency)} a year`
}

function tierCap(tier: SubscriptionTier): string {
  if (tier.annualBookings == null) return 'Volume agreed case by case'

  // La cifra semanal es una referencia para dimensionarlo, no un segundo
  // límite: lo que se mide y lo que se cobra es el total del año.
  return `${tier.annualBookings} bookings a year · about ${Math.round(tier.annualBookings / 52)} a week`
}
</script>

<template>
  <div>
    <div
      v-if="loading && !subscription"
      class="d-flex justify-center py-16"
    >
      <VProgressCircular
        indeterminate
        color="primary"
      />
    </div>

    <VAlert
      v-else-if="unavailable"
      type="info"
      variant="tonal"
    >
      Subscriptions are not set up for this dive center yet.
    </VAlert>

    <template v-else-if="subscription && usage">
      <VRow>
        <VCol
          cols="12"
          md="7"
        >
          <VCard>
            <VCardItem>
              <VCardTitle>{{ subscription.tier.displayName }}</VCardTitle>
              <!-- El nombre del centro va en el subtítulo: el plan es suyo y no
                   de la cuenta, y sin decirlo se lee como si fuera de toda la
                   empresa. -->
              <VCardSubtitle>
                {{ current?.name }} ·
                {{ formatDate(subscription.periodStart) }} — {{ formatDate(subscription.periodEnd) }}
                <span v-if="subscription.cancelAtPeriodEnd"> · not renewing</span>
              </VCardSubtitle>
            </VCardItem>

            <VCardText>
              <div class="d-flex align-baseline justify-space-between mb-2">
                <span class="text-h4">{{ usage.seats }}</span>
                <span
                  v-if="cap != null"
                  class="text-body-2 text-medium-emphasis"
                >
                  of {{ cap }} bookings · {{ percent }}%
                </span>
                <span
                  v-else
                  class="text-body-2 text-medium-emphasis"
                >
                  bookings this year · custom allowance
                </span>
              </div>

              <VProgressLinear
                v-if="cap != null"
                :model-value="barValue"
                :color="barColor"
                height="8"
                rounded
              />

              <!-- Una reserva no es un documento sino una persona en una
                   actividad, y quien mira este número tiene que poder atarlo a
                   lo que ve en su calendario. -->
              <p class="text-caption text-medium-emphasis mt-3 mb-0">
                One booking is one person on one activity. A diver doing two dives
                a day for five days is ten bookings. Cancelled bookings do not count.
              </p>

              <template v-if="advice">
                <VDivider class="my-4" />

                <p class="text-body-2 mb-1">
                  {{ advice.text }}
                </p>
                <p
                  v-if="advice.hint"
                  class="text-caption text-medium-emphasis mb-0"
                >
                  {{ advice.hint }}
                </p>
              </template>

              <template v-if="usage.state !== 'ok'">
                <VDivider class="my-4" />

                <p class="text-body-2 mb-0">
                  <template v-if="usage.state === 'blocked' && subscription.enforced">
                    You are past the {{ subscription.tier.overshootTolerancePct }}% margin, so
                    new bookings are paused. Everything else — the calendar, rosters,
                    day sheets and medical forms — stays open.
                  </template>
                  <template v-else>
                    You are over the allowance but still within the
                    {{ subscription.tier.overshootTolerancePct }}% margin, so you can keep
                    taking bookings.
                  </template>
                </p>
              </template>
            </VCardText>
          </VCard>

          <VCard
            v-if="recentWeeks.length"
            class="mt-6"
          >
            <VCardItem>
              <VCardTitle>Recent weeks</VCardTitle>
              <VCardSubtitle>Bookings by week of activity</VCardSubtitle>
            </VCardItem>

            <VCardText>
              <div
                v-for="w in recentWeeks"
                :key="w.week"
                class="d-flex align-center ga-3 mb-2"
              >
                <span
                  class="text-caption text-medium-emphasis"
                  style="min-inline-size: 72px"
                >{{ w.week }}</span>
                <VProgressLinear
                  :model-value="busiestWeek ? (w.seats / busiestWeek) * 100 : 0"
                  color="primary"
                  height="6"
                  rounded
                />
                <span
                  class="text-caption"
                  style="min-inline-size: 32px; text-align: end"
                >{{ w.seats }}</span>
              </div>
            </VCardText>
          </VCard>
        </VCol>

        <VCol
          cols="12"
          md="5"
        >
          <VCard>
            <VCardItem>
              <VCardTitle>Plans</VCardTitle>
              <!-- Sin botón de contratar: el plan lo asigna Blue Trails. Un
                   botón que abre un mail es peor que una frase que lo explica. -->
              <VCardSubtitle>Talk to us to move between plans.</VCardSubtitle>
            </VCardItem>

            <VCardText>
              <div
                v-for="tier in subscription.tiers"
                :key="tier.slug"
                class="pa-3 mb-3 rounded border"
                :class="tier.current ? 'border-primary' : ''"
              >
                <div class="d-flex align-center justify-space-between">
                  <span class="font-weight-medium">{{ tier.displayName }}</span>
                  <VChip
                    v-if="tier.current"
                    color="primary"
                    size="x-small"
                  >
                    Current
                  </VChip>
                </div>

                <div class="text-body-2 text-medium-emphasis mt-1">
                  {{ tierCap(tier) }}
                </div>
                <div class="text-body-2 mt-1">
                  {{ tierPrice(tier) }}
                </div>
              </div>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </template>
  </div>
</template>
